import React, { useEffect, useState, useContext } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const VendorReminderTemplatePage: React.FunctionComponent = () => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [placeholders, setPlaceholders] = useState<string[]>([]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get('/vendors/vendor_reminder_template');
      setSubject(response.data?.subject || '');
      setBody(response.data?.body || '');
      setPlaceholders(response.data?.placeholders || []);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to load template';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/vendors/vendor_reminder_template', {
        vendor_reminder_template: { subject, body },
      });
      showSuccessSnackBar('Template saved');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to save template';
      showErrorSnackBar(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box style={{ width: '100%' }}>
      <Typography variant="h5" component="h5" sx={{ mt: 1 }}>
        Reminder Email Template
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, mb: 2 }}>
        Customize the default subject and body used when sending vendor reminders.
      </Typography>

      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Available placeholders
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                {placeholders.map((p) => (
                  <Chip key={p} label={`{{${p}}}`} size="small" />
                ))}
              </Box>
            </Box>
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              minRows={14}
              size="small"
              InputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Template'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorReminderTemplatePage;
